<template>
    <span class="ready-for-inspection">
        <a href="#"
           class="ready-link"
           :title="'Mark quote ' + quoteId + ' as ready for inspection'"
           @click.prevent.stop="openModal()">
            <i class="fas fa-clipboard-check"></i>
        </a>

        <v-easy-dialog v-model="open">
            <div class="v-easy-dialog-wrap">
                <div class="v-easy-dialog-title">
                    Ready for Inspection — Quote #{{ quoteId }}
                </div>

                <div v-if="loading" class="text-muted" style="padding: 12px 0;">
                    Checking unticked processes…
                </div>

                <div v-else-if="error" class="alert alert-danger" style="margin: 8px 0;">
                    {{ error }}
                </div>

                <div v-else>
                    <p v-if="untickedRows === 0">
                        All processes are ticked. Queue this quote for QC inspection?
                    </p>
                    <div v-else>
                        <p>
                            <strong>{{ untickedRows }} process{{ untickedRows === 1 ? '' : 'es' }}</strong>
                            still unticked. Mark as ready anyway?
                        </p>
                        <ul style="max-height: 180px; overflow-y: auto; background: #fafafa; padding: 8px 24px; font-size: 13px;">
                            <li v-for="row in unticked" :key="row.id">{{ row.process_name }}</li>
                        </ul>
                    </div>
                </div>

                <div class="v-easy-dialog-controls" style="margin-top: 12px;">
                    <button class="btn btn-default" :disabled="submitting" @click="open = false">Cancel</button>
                    <button class="btn btn-primary"
                            :disabled="loading || submitting || error"
                            @click="confirm()">
                        <span v-if="submitting">Submitting…</span>
                        <span v-else>Confirm — Ready for Inspection</span>
                    </button>
                </div>
            </div>
        </v-easy-dialog>
    </span>
</template>

<script>
import VEasyDialog from 'v-easy-dialog';

/*
    "Ready for Inspection" trigger — visible to Block 3 submitters
    (Tim / Peter / Cameron / John). Click opens a modal that previews
    any still-unticked processes for the quote, then on confirm POSTs
    to /production-checklist/{quote_id}/ready.

    The backend endpoint is idempotent — clicking twice (or from two
    different items in the same quote) creates the signoff row once.

    See planning/quote-signoff-design.md §7.6.
*/
export default {
    components: { VEasyDialog },
    props: {
        quoteId: { type: Number, required: true },
    },
    data() {
        return {
            open: false,
            loading: false,
            submitting: false,
            unticked: [],
            untickedRows: 0,
            error: null,
        };
    },
    watch: {
        open(isOpen) {
            if (isOpen) this.loadPreview();
        },
    },
    methods: {
        openModal() {
            this.open = true;
        },
        loadPreview() {
            this.loading = true;
            this.error = null;
            this.unticked = [];
            this.untickedRows = 0;
            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/production-checklist/${this.quoteId}/ready?preview=1`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    this.loading = false;
                    if (status >= 400) {
                        this.error = body.message || body.error || 'Could not load preview.';
                        return;
                    }
                    this.unticked = body.unticked || [];
                    this.untickedRows = body.unticked_rows || 0;
                })
                .catch(err => {
                    this.loading = false;
                    this.error = err.message || 'Network error';
                });
        },
        confirm() {
            if (this.submitting) return;
            this.submitting = true;
            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/production-checklist/${this.quoteId}/ready`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    this.submitting = false;
                    if (status >= 400) {
                        this.error = body.message || body.error || 'Submit failed.';
                        return;
                    }
                    this.open = false;
                    this.$emit('submitted', { quoteId: this.quoteId, untickedRows: body.unticked_rows });
                })
                .catch(err => {
                    this.submitting = false;
                    this.error = err.message || 'Network error';
                });
        },
    },
};
</script>

<style scoped>
    .ready-for-inspection {
        display: inline-block;
        margin: 0 8px;
    }
    .ready-link {
        color: #1a7e3a;
        font-size: 18px;
        text-decoration: none;
    }
    .ready-link:hover {
        color: #14591b;
    }
</style>
